import { Reservation, Task } from "~/packages/taskrouter";
import { ConversationImpl, Conversation } from "~/modules/Conversation";
import { OutboundTaskHandler } from "~/modules/actions/OutboundTaskHandler";
import { ConversationsSdkImpl } from "~/modules/conversations/ConversationsSdkImpl";
import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { CbmImpl } from "~/modules/cbm";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { sendTrackingEvent } from "~/modules/actions/ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";




export interface ResumeConversationChannelResponse {
    task: Task;
    conversation: Conversation;
}





























export class ResumeConversationChannel implements Action<Promise<ResumeConversationChannelResponse>> {
    


    #interactionSid: string;

    


    #channelSid: string;

    constructor(interactionSid: string, channelSid: string) {
        this.#interactionSid = interactionSid;
        this.#channelSid = channelSid;
    }

    
    async run(ctx: {}): Promise<ResumeConversationChannelResponse> {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const conversations = context.retrieveInstanceOf(ContextInstance.ConversationsSdkImpl, ConversationsSdkImpl);
        const conversationInitPromise = conversations.init();

        logger.debug(
            `resumeConversationChannel invoked with interactionSid: ${this.#interactionSid} and channelSid: ${
                this.#channelSid
            }`
        );

        if (!this.#interactionSid) {
            const errorMsg = `resumeConversationChannel: interactionSid is required`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.ResumeConversationChannelCompleted, analytics, worker, {
                error: errorMsg,
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!this.#channelSid) {
            const errorMsg = `resumeConversationChannel: channelSid is required`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.ResumeConversationChannelCompleted, analytics, worker, {
                error: errorMsg,
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const outboundTaskHandler = new OutboundTaskHandler(worker);
        outboundTaskHandler.startListeningReservations();
        try {
            await cbm.resumeChannel(this.#interactionSid, this.#channelSid, "active");

            sendTrackingEvent(EVENTS.ResumeConversationChannelCompleted, analytics, worker);
        } catch (e) {
            const errorMsg = `resumeConversationChannel: Failed to resume conversation channel for interactionSid ${
                this.#interactionSid
            } and channelSid ${this.#channelSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.ResumeConversationChannelCompleted, analytics, worker, {
                error: errorMsg,
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        let reservation: Reservation;
        try {
            reservation = await outboundTaskHandler.waitForReservation(
                (r) =>
                    r.task.attributes.flexInteractionSid === this.#interactionSid &&
                    r.task.attributes.flexInteractionChannelSid === this.#channelSid
            );
        } catch (error) {
            const errorMsg = `resumeConversationChannel: Failed to get reservation for interaction ${
                this.#interactionSid
            } and channelSid ${this.#channelSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.ResumeConversationChannelCompleted, analytics, worker, {
                error: errorMsg,
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            reservation = await cbm.acceptReservation(reservation);
        } catch (error) {
            const errorMsg = `resumeConversationChannel: Failed to accept reservation ${
                reservation.sid
            } for the interaction ${this.#interactionSid} and channelSid ${this.#channelSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.ResumeConversationChannelCompleted, analytics, worker, {
                error: errorMsg,
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid,
                reservationSid: reservation.sid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        let conversation: Conversation;
        const conversationSid = reservation.task.attributes.conversationSid;
        try {
            await conversationInitPromise;
            const twilioConversation = await conversations.getConversationBySid(conversationSid);
            conversation = new ConversationImpl(context, twilioConversation);
            sendTrackingEvent(EVENTS.ResumeConversationChannelCompleted, analytics, worker, {
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid,
                reservationSid: reservation.sid,
                conversationSid
            });
        } catch (error) {
            const errorMsg = `resumeConversationChannel: Failed to fetch conversation ${conversationSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.ResumeConversationChannelCompleted, analytics, worker, {
                error: errorMsg,
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid,
                reservationSid: reservation.sid,
                conversationSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        return { task: reservation.task, conversation };
    }
}
